import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, X, AlertCircle, Settings2, CheckCircle2 } from 'lucide-react';
import { dynamicSupplyRaw } from '../api';

const FIELD_OPTIONS = [
  { key: null,               label: '— تجاهل —',           color: '#94A3B8' },
  { key: 'school_id',        label: 'الرقم الوزاري',       color: '#0F766E' },
  { key: 'school_name',      label: 'اسم المدرسة',         color: '#0F766E' },
  { key: 'region',           label: 'المنطقة',             color: '#334155' },
  { key: 'governorate',      label: 'إدارة التعليم',       color: '#334155' },
  { key: 'stage',            label: 'المرحلة',             color: '#334155' },
  { key: 'delivery_date',    label: 'تاريخ التوريد',       color: '#D97706' },
  { key: 'status',           label: 'حالة التوريد',        color: '#D97706' },
  { key: 'notes',            label: 'ملاحظات',             color: '#94A3B8' },
  { key: 'item',             label: 'صنف (كمية)',          color: '#14B8A6' },
];

const ROW_ROLE_OPTIONS = [
  { key: null,             label: '— بيانات مدرسة —',      color: '#94A3B8' },
  { key: 'header',         label: 'صف العناوين',            color: '#0F766E' },
  { key: 'unit_price',     label: 'صف سعر الوحدة',          color: '#D97706' },
  { key: 'supplier',       label: 'صف اسم المورد',          color: '#14B8A6' },
];

/**
 * ManualMappingModal — analyst-driven column mapping when the auto-parser
 * cannot confidently detect the workbook shape. Shows the first ~20 rows
 * of the selected sheet as a grid, lets the analyst assign one role to
 * each column and each row, and emits a `mapping` object compatible with
 * /api/supply/dynamic-commit-manual.
 */
export default function ManualMappingModal({ token, onCancel, onProceed }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [raw, setRaw] = useState(null);
  const [sheet, setSheet] = useState(null);
  // column role: index → { role: 'school_id'|'school_name'|...|'item'|null }
  const [colRoles, setColRoles] = useState({});
  const [rowRoles, setRowRoles] = useState({});
  const [supplier, setSupplier] = useState('');

  const loadSheet = async (sheetName) => {
    setLoading(true); setError(null);
    try {
      const d = await dynamicSupplyRaw({ token, sheet: sheetName, limit: 20 });
      setRaw(d); setSheet(d.picked_sheet);
      const h = d.hints || {};
      // Pre-fill from hints
      const cr = {};
      if (h.school_id_col   != null) cr[h.school_id_col]   = 'school_id';
      if (h.school_name_col != null) cr[h.school_name_col] = 'school_name';
      for (const c of (h.item_cols || [])) cr[c] = 'item';
      setColRoles(cr);
      const rr = {};
      if (h.header_row     != null) rr[h.header_row]     = 'header';
      if (h.unit_price_row != null) rr[h.unit_price_row] = 'unit_price';
      if (h.supplier_row   != null) rr[h.supplier_row]   = 'supplier';
      setRowRoles(rr);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadSheet(null); /* eslint-disable-next-line */ }, []);

  const setCol = (idx, role) => setColRoles((s) => ({ ...s, [idx]: role }));
  const setRow = (idx, role) => setRowRoles((s) => ({ ...s, [idx]: role }));

  const summary = useMemo(() => {
    const header_row = Object.entries(rowRoles).find(([, v]) => v === 'header')?.[0];
    const unit_price_row = Object.entries(rowRoles).find(([, v]) => v === 'unit_price')?.[0];
    const supplier_row = Object.entries(rowRoles).find(([, v]) => v === 'supplier')?.[0];
    const school_id_col = Object.entries(colRoles).find(([, v]) => v === 'school_id')?.[0];
    const school_name_col = Object.entries(colRoles).find(([, v]) => v === 'school_name')?.[0];
    const region_col = Object.entries(colRoles).find(([, v]) => v === 'region')?.[0];
    const governorate_col = Object.entries(colRoles).find(([, v]) => v === 'governorate')?.[0];
    const stage_col = Object.entries(colRoles).find(([, v]) => v === 'stage')?.[0];
    const delivery_date_col = Object.entries(colRoles).find(([, v]) => v === 'delivery_date')?.[0];
    const status_col = Object.entries(colRoles).find(([, v]) => v === 'status')?.[0];
    const notes_col = Object.entries(colRoles).find(([, v]) => v === 'notes')?.[0];
    const item_cols = Object.entries(colRoles).filter(([, v]) => v === 'item').map(([k]) => parseInt(k, 10));
    return {
      sheet,
      header_row: header_row != null ? parseInt(header_row, 10) : null,
      unit_price_row: unit_price_row != null ? parseInt(unit_price_row, 10) : null,
      supplier_row: supplier_row != null ? parseInt(supplier_row, 10) : null,
      school_id_col: school_id_col != null ? parseInt(school_id_col, 10) : null,
      school_name_col: school_name_col != null ? parseInt(school_name_col, 10) : null,
      region_col: region_col != null ? parseInt(region_col, 10) : null,
      governorate_col: governorate_col != null ? parseInt(governorate_col, 10) : null,
      stage_col: stage_col != null ? parseInt(stage_col, 10) : null,
      delivery_date_col: delivery_date_col != null ? parseInt(delivery_date_col, 10) : null,
      status_col: status_col != null ? parseInt(status_col, 10) : null,
      notes_col: notes_col != null ? parseInt(notes_col, 10) : null,
      item_cols,
    };
  }, [colRoles, rowRoles, sheet]);

  const canProceed = summary.header_row != null && (summary.school_id_col != null || summary.school_name_col != null)
    && summary.item_cols.length > 0 && supplier.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onCancel}
      data-testid="manual-mapping-modal">
      <div className="se-panel-hi max-w-[1400px] w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ padding: 22 }}>
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[11px] font-semibold" style={{ color: 'var(--se-emerald)' }}>MANUAL MAPPING</div>
            <div className="text-[18px] font-bold text-[color:var(--se-ink)] mt-1 flex items-center gap-2">
              <Settings2 size={16} style={{ color: 'var(--se-emerald)' }} /> ربط الأعمدة يدوياً
            </div>
            <div className="text-slate-500 text-[12px] mt-1">
              اختر دور كل صف (عناوين، سعر، مورد) ودور كل عمود (رقم وزاري، اسم مدرسة، صنف...) ثم تابع للمعاينة.
            </div>
          </div>
          <button onClick={onCancel} className="se-btn se-btn-ghost" data-testid="mapping-close">
            <X size={13} /> إغلاق
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center flex-1">
            <Loader2 size={26} className="animate-spin mx-auto mb-2" style={{ color: 'var(--se-emerald)' }} />
            <div className="text-slate-600 text-[13px]">جاري تحميل الورقة...</div>
          </div>
        ) : error ? (
          <div className="p-6 flex-1" style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10 }}>
            <div className="flex items-start gap-2 text-red-900">
              <AlertCircle size={16} className="mt-0.5" />
              <div>{error}</div>
            </div>
          </div>
        ) : (
          <>
            {/* Sheet picker + supplier */}
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-slate-600 text-[12px] font-medium">ورقة العمل:</label>
                <select className="se-input" value={sheet || ''}
                  onChange={(e) => loadSheet(e.target.value)} data-testid="mapping-sheet-select">
                  {(raw?.sheets || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-[300px]">
                <label className="text-slate-600 text-[12px] font-medium">اسم المورد:</label>
                <input className="se-input flex-1" value={supplier} onChange={(e) => setSupplier(e.target.value)}
                  placeholder="مطلوب — أدخل اسم المورد" data-testid="mapping-supplier" />
              </div>
              <div className="text-slate-500 text-[11.5px]">
                {raw?.n_rows_total?.toLocaleString('en-US')} صف · {raw?.n_cols} عمود
              </div>
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-auto se-panel" style={{ padding: 0, maxHeight: 500 }}>
              <table className="text-[11px]" style={{ borderCollapse: 'collapse', direction: 'rtl' }}>
                <thead>
                  <tr>
                    <th className="sticky top-0 bg-white z-10 p-1 text-slate-400 border-b border-l" style={{ borderColor: 'var(--se-border)', minWidth: 110 }}>
                      # / دور
                    </th>
                    {Array.from({ length: raw?.n_cols || 0 }, (_, ci) => (
                      <th key={ci} className="sticky top-0 bg-white z-10 p-1 border-b border-l"
                          style={{ borderColor: 'var(--se-border)', minWidth: 140 }}>
                        <div className="text-slate-400 text-[10px] mb-1">عمود {ci + 1}</div>
                        <select value={colRoles[ci] || ''} onChange={(e) => setCol(ci, e.target.value || null)}
                          data-testid={`col-role-${ci}`}
                          className="text-[10.5px] px-1 py-0.5 rounded border w-full"
                          style={{ borderColor: 'var(--se-border)', background: colRoles[ci] ? '#F0FDFA' : '#FFFFFF' }}>
                          {FIELD_OPTIONS.map((o) => <option key={o.key ?? '_'} value={o.key ?? ''}>{o.label}</option>)}
                        </select>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(raw?.grid || []).map((row, ri) => (
                    <tr key={ri}>
                      <td className="sticky right-0 bg-white p-1 border-b border-l" style={{ borderColor: 'var(--se-border)', zIndex: 5 }}>
                        <div className="text-slate-500 text-[10.5px] mb-1">صف {ri + 1}</div>
                        <select value={rowRoles[ri] || ''} onChange={(e) => setRow(ri, e.target.value || null)}
                          data-testid={`row-role-${ri}`}
                          className="text-[10.5px] px-1 py-0.5 rounded border w-full"
                          style={{ borderColor: 'var(--se-border)',
                                   background: rowRoles[ri] === 'header' ? '#ECFDF5' : rowRoles[ri] === 'unit_price' ? '#FFFBEB' : rowRoles[ri] === 'supplier' ? '#F0FDFA' : '#FFFFFF' }}>
                          {ROW_ROLE_OPTIONS.map((o) => <option key={o.key ?? '_'} value={o.key ?? ''}>{o.label}</option>)}
                        </select>
                      </td>
                      {row.map((cell, ci) => {
                        const colRole = colRoles[ci];
                        const rowRole = rowRoles[ri];
                        let bg = '#FFFFFF';
                        if (rowRole === 'header') bg = '#ECFDF5';
                        else if (rowRole === 'unit_price') bg = '#FFFBEB';
                        else if (rowRole === 'supplier') bg = '#F0FDFA';
                        else if (colRole === 'item') bg = '#F5F3FF';
                        else if (colRole === 'school_id' || colRole === 'school_name') bg = '#ECFDF5';
                        return (
                          <td key={ci} className="p-1 border-b border-l align-top"
                              style={{ borderColor: 'var(--se-border)', background: bg, maxWidth: 200 }}
                              title={cell}>
                            <div className="truncate text-slate-700">{cell || '·'}</div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer with summary + proceed */}
            <div className="mt-4 pt-3 border-t flex items-center justify-between flex-wrap gap-3" style={{ borderColor: 'var(--se-border)' }}>
              <div className="text-[11.5px] text-slate-600">
                <span className="font-semibold text-slate-800">التلخيص:</span>
                {' '}صف العناوين: <b>{summary.header_row != null ? summary.header_row + 1 : '—'}</b> ·
                {' '}الرقم الوزاري: <b>{summary.school_id_col != null ? summary.school_id_col + 1 : '—'}</b> ·
                {' '}اسم المدرسة: <b>{summary.school_name_col != null ? summary.school_name_col + 1 : '—'}</b> ·
                {' '}عدد الأصناف: <b style={{ color: 'var(--se-emerald)' }}>{summary.item_cols.length}</b>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onCancel} className="se-btn se-btn-ghost">إلغاء</button>
                <button onClick={() => onProceed({ mapping: summary, supplier: supplier.trim() })}
                  disabled={!canProceed}
                  className="se-btn se-btn-primary disabled:opacity-40"
                  data-testid="mapping-proceed">
                  <CheckCircle2 size={13} /> متابعة إلى معاينة التغييرات
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
